'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DefaultAdults, DefaultChildren, useSearch } from '@/context/listing-search-provider';
import calculateSubtotalRateAndDates from '@/utils/calculate-subtotal-rate-and-dates';
import { createSearchDateLabel } from '@/utils/create-search-date-label';
import formatCurrency from '@/utils/format-currency';
import { getListingPath } from '@/utils/paths';
import { pluralize } from '@/utils/pluralize';
import {
  Listing,
  ListingLos,
} from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';
import { parseISO } from 'date-fns';
import { sum } from 'lodash';
import { ClientBrandConfig } from 'types/brand-config';

import { ListingRateRange } from './listing-rate-range';

interface ListingRateProps {
  listingLos?: ListingLos;
  brand: ClientBrandConfig;
  listing: Listing;
  hasFlexableRates: boolean;
  size?: string;
  setListingUrl(a: string): void;
}

const ListingRate: React.FC<ListingRateProps> = ({
  listingLos,
  listing,
  brand,
  hasFlexableRates,
  size = 'md',
  setListingUrl,
}) => {
  const { adults, children } = useSearch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [pickerActive, setPickerActive] = useState(false);
  const [activeScroll, setActiveScroll] = useState(false);
  const stays = useMemo(
    () => (listingLos ? calculateSubtotalRateAndDates(listingLos) : []),
    [listingLos, listing]
  );
  useEffect(() => {
    if (!hasFlexableRates) {
      return;
    }
    setActiveScroll(true);
    const timeout = setTimeout(() => {
      setActiveScroll(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [currentIndex, hasFlexableRates]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex === 0 ? 0 : prevIndex - 1;
      scrollToIndex(newIndex);
      return newIndex;
    });
  }, [stays]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      const newIndex =
        prevIndex === stays.length - 1 ? stays.length - 1 : prevIndex + 1;
      scrollToIndex(newIndex);
      return newIndex;
    });
  }, [stays]);

  // Touch event handlers (Mobile)
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.stopPropagation();
      touchStartY.current = e.touches[0].clientY;
    },
    [pickerActive]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.stopPropagation();
      touchEndY.current = e.changedTouches[0].clientY;
      const swipeThreshold = 10; // Adjusted for better swipe detection
      if (touchStartY.current - touchEndY.current > swipeThreshold) {
        goToNext();
      } else if (touchEndY.current - touchStartY.current > swipeThreshold) {
        goToPrevious();
      }
    },
    [goToNext, goToPrevious, pickerActive]
  );

  const scrollToIndex = (index: number) => {
    if (carouselRef.current) {
      const element = carouselRef.current.children[index] as HTMLElement;
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Scroll event handler
  useEffect(() => {
    if (carouselRef.current) {
      const options = {
        root: carouselRef.current,
        threshold: 0.5, // Trigger when 50% of the element is visible
      };

      // Create a new IntersectionObserver instance
      observerRef.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const newIndex = Array.from(carouselRef.current!.children).indexOf(
              entry.target
            );
            setCurrentIndex(newIndex);
          }
        });
      }, options);

      // Observe each child of the carousel
      Array.from(carouselRef.current.children).forEach((child) => {
        observerRef.current?.observe(child);
      });

      return () => {
        // Cleanup the observer when the component unmounts
        observerRef.current?.disconnect();
      };
    }
  }, [stays.length, pickerActive]);

  // Calculate the listing URL during render instead of in useEffect
  const listingUrl = useMemo(() => {
    if (!listing) {
      return '';
    }
    const basePath = getListingPath(listing, brand);
    const stayParams = stays[currentIndex];
    const search = new URLSearchParams();
    const effectiveAdults = adults ?? DefaultAdults;
    const effectiveChildren = children ?? DefaultChildren;

    if (effectiveAdults !== DefaultAdults) {
      search.set('adults', effectiveAdults.toString());
    }
    if (effectiveChildren !== DefaultChildren) {
      search.set('children', effectiveChildren.toString());
    }

    if (stayParams) {
      // Update the arrive and depart query params on new link path to reflect the selected stay
      search.set('arrive', stayParams.check_in);
      search.set('depart', stayParams.check_out);
    }

    if (search.size > 0) {
      // Return the URL with the new search params
      return basePath + '?' + search.toString();
    } else {
      // Return the base path
      return basePath;
    }
  }, [currentIndex, listing, adults, children, stays, brand]);

  // Only use useEffect to notify parent when URL changes (side effect)
  useEffect(() => {
    if (listingUrl) {
      setListingUrl(listingUrl);
    }
  }, [listingUrl, setListingUrl]);

  // If no stays, show rate range.
  if (!stays.length) {
    if (!brand.showPriceRanges) {
      return <div className="flex h-[.125rem] w-full"></div>;
    }
    return (
      <ListingRateRange
        listing={listing}
        className={clsx(
          'flex h-[3rem] w-full flex-col items-end justify-start',
          !brand.showPriceRanges && 'invisible'
        )}
      />
    );
  }

  return (
    <div
      className={clsx(
        'listing-rate relative self-stretch',
        hasFlexableRates && 'py-[.25rem]'
      )}
    >
      {stays.length > 1 && currentIndex !== 0 && hasFlexableRates && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goToPrevious();
          }}
          className="absolute left-1/2 top-0 m-0 -translate-x-1/2 -rotate-90 transform rounded-full p-0 text-gray-8 focus-visible:outline-none"
        >
          <svg
            className="ml-2"
            width="6"
            height="9.4"
            viewBox="0 0 14 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="currentColor"
              d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
            />
          </svg>
        </button>
      )}
      <div
        className={clsx('mx-auto w-full')}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={carouselRef}
          className={clsx(
            pickerActive ? 'overflow-y-scroll' : 'overflow-hidden',
            'no-scrollbar flex h-[3.5rem] snap-y snap-mandatory flex-col gap-1 overscroll-none scroll-smooth transition-transform duration-500 ease-in-out'
          )}
        >
          {stays.map((stay, i) => {
            return (
              <div
                key={`${listing?.id}_${i}_rate_option`}
                className={clsx(
                  'box-border h-[3.5rem] rounded-xl py-[.25rem]',
                  pickerActive &&
                    'hover:bg-neutral-100 hover:shadow-md hover:ring-1 hover:ring-inset hover:ring-link',
                  activeScroll && '!cursor-ns-resize bg-neutral-100 shadow-md',
                  stay.discount > 0 && 'has-tooltip'
                )}
              >
                <section
                  tabIndex={i + 1}
                  className={clsx(
                    'flex max-h-full w-full snap-center snap-always flex-col self-stretch px-[0.5rem] focus-visible:outline-none'
                  )}
                >
                  <div
                    className={clsx(
                      stay.discount > 0 ? 'visible' : 'invisible',
                      'flex flex-row items-center justify-end'
                    )}
                  >
                    <span
                      className={clsx(
                        `ut-xxs self-end font-medium !leading-none text-gray-5 line-through sm:font-bold`
                      )}
                    >
                      {formatCurrency(
                        sum([stay.discount, stay.subtotal]),
                        true
                      )}
                    </span>
                  </div>
                  <div className="flex flex-row items-end justify-between">
                    {hasFlexableRates && (
                      <div
                        className={clsx(
                          'flex h-[3.5rem] flex-col justify-start text-left',
                          !hasFlexableRates && 'invisible'
                        )}
                      >
                        <span
                          className={`ut-${size} whitespace-nowrap font-semibold leading-none lg:font-bold`}
                        >
                          {createSearchDateLabel(
                            parseISO(stay.check_in),
                            parseISO(stay.check_out)
                          )}
                        </span>
                        {stays.length > 1 &&
                          currentIndex !== stays.length - 1 &&
                          hasFlexableRates &&
                          currentIndex === 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToNext();
                                setPickerActive(true);
                              }}
                              className={clsx(
                                'ut-xxs m-0 transform rounded-full p-0 text-gray-8 focus-visible:outline-none'
                              )}
                            >
                              <span className="ut-xxs whitespace-nowrap font-medium text-link underline">
                                {`${stays.length - 1} more ${pluralize('stay', stays.length - 1)}`}
                              </span>
                            </button>
                          )}
                      </div>
                    )}
                    <div
                      className={clsx(
                        'relative flex h-[3.5rem] w-full flex-col items-end',
                        hasFlexableRates && 'w-fit text-right'
                      )}
                    >
                      <div className={`flex flex-col`}>
                        <span
                          className={clsx(
                            `ut-${size} font-bold !leading-none text-success-main`
                          )}
                        >
                          {formatCurrency(stay.subtotal, true)}
                        </span>
                      </div>
                      <span className="ut-xxs whitespace-nowrap font-medium">
                        total before taxes
                      </span>
                    </div>
                  </div>
                </section>
              </div>
            );
          })}
        </div>
      </div>
      {stays.length > 1 &&
        currentIndex !== stays.length - 1 &&
        hasFlexableRates &&
        currentIndex !== 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className={clsx(
              'ut-xxs absolute -bottom-1 left-1/2 m-0 -translate-x-1/2 transform rounded-full p-0 text-gray-8 focus-visible:outline-none'
            )}
          >
            <svg
              className="rotate-90"
              width="6"
              height="9.4"
              viewBox="0 0 14 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="currentColor"
                d="M13.2103 13.2103C13.8798 12.5409 13.8798 11.4538 13.2103 10.7843L2.92807 0.502063C2.25865 -0.167356 1.17151 -0.167356 0.502095 0.502063C-0.167323 1.17148 -0.167323 2.25862 0.502095 2.92804L9.57406 12L0.507448 21.072C-0.16197 21.7414 -0.16197 22.8285 0.507448 23.4979C1.17687 24.1674 2.264 24.1674 2.93342 23.4979L13.2157 13.2157L13.2103 13.2103Z"
              />
            </svg>
          </button>
        )}
    </div>
  );
};

export default ListingRate;
